import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import FirmIcon from "../../components/FirmIcon";
import { Field, TextInput, ModalShell, DeleteButton } from "./ui";

const EMPTY = {
  name: "", tagline: "", description: "", website_url: "", icon: "Activity", logo: "", rating: 4.5,
  category: "CFD", country: "", discount_label: "", discount_code: "FTC",
  founded: "", headquarters: "", min_price: "", max_capital: "", profit_split: "",
  payout_frequency: "", prices_verified: "", payouts_paid: "", traders_count: "", featured: false,
  pros: "", cons: "", platforms: "", instruments: "", challenges: "",
};

const ICON_CHOICES = ["Triangle", "Hexagon", "Zap", "Shield", "Activity", "Target", "TrendingUp", "Globe", "Gauge", "BarChart3", "Layers"];

const splitList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

const parseChallenges = (s) =>
  s.split("\n")
    .map((l) => l.split("|").map((x) => x.trim()))
    .filter((p) => p[0])
    .map((p) => ({ size: p[0] || "", price: p[1] || "", target: p[2] || "", daily_dd: p[3] || "", max_dd: p[4] || "", model: p[5] || "", price_bw: p[6] || "", discount_label: p[7] || "" }));

const formatChallenges = (list) =>
  (list || []).map((c) => [c.size, c.price, c.target, c.daily_dd, c.max_dd, c.model || "", c.price_bw || "", c.discount_label || ""].join(" | ").replace(/( \| )+$/, "")).join("\n");

function firmToForm(f) {
  return {
    ...EMPTY, ...f,
    logo: f.logo || "",
    rating: f.rating ?? 4.5,
    pros: (f.pros || []).join(", "),
    cons: (f.cons || []).join(", "),
    platforms: (f.platforms || []).join(", "),
    instruments: (f.instruments || []).join(", "),
    challenges: formatChallenges(f.challenges),
  };
}

export default function FirmsTab() {
  const [firms, setFirms] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | firm
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/firms").then(({ data }) => setFirms(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (f) => { setForm(firmToForm(f)); setEditing(f); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      rating: parseFloat(form.rating) || 0,
      pros: splitList(form.pros), cons: splitList(form.cons),
      platforms: splitList(form.platforms), instruments: splitList(form.instruments),
      challenges: parseChallenges(form.challenges),
    };
    try {
      if (editing === "new") {
        await api.post("/admin/firms", payload);
        toast.success("FIRM ADDED TO INDEX");
      } else {
        await api.put(`/admin/firms/${editing.id}`, payload);
        toast.success("FIRM UPDATED");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    try {
      await api.delete(`/admin/firms/${f.id}`);
      toast.success(`${f.name.toUpperCase()} DELISTED`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-firms-tab">
      <div className="mb-6 flex justify-end">
        <button onClick={openNew} data-testid="add-firm-btn"
          className="btn-beam btn-gradient inline-flex items-center gap-2 px-5 py-2.5 font-mono text-[10px] font-700 uppercase tracking-[0.2em] text-white">
          <Plus size={13} /> Add Firm
        </button>
      </div>
      <div className="overflow-x-auto border border-line" data-testid="admin-firms-table">
        <table className="w-full min-w-[760px] border-collapse font-mono text-xs">
          <thead>
            <tr className="border-b border-line bg-elevate text-left text-[10px] uppercase tracking-[0.2em] text-muted">
              <th className="px-4 py-3.5 font-500">Firm</th>
              <th className="px-4 py-3.5 font-500">Category</th>
              <th className="px-4 py-3.5 font-500">Score</th>
              <th className="px-4 py-3.5 font-500">Code</th>
              <th className="px-4 py-3.5 font-500">Challenges</th>
              <th className="px-4 py-3.5 font-500">Reviews</th>
              <th className="px-4 py-3.5 font-500 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {firms.map((f) => (
              <tr key={f.id} className="border-b border-line/60 text-muted hover:bg-white/[0.02]" data-testid={`admin-firm-row-${f.slug}`}>
                <td className="px-4 py-3.5">
                  <span className="flex items-center gap-2.5 text-white">
                    <span className="flex h-7 w-7 items-center justify-center overflow-hidden border border-line bg-elevate"><FirmIcon name={f.icon} size={13} logo={f.logo} /></span>
                    {f.name}
                  </span>
                </td>
                <td className="px-4 py-3.5">{f.category || "CFD"}</td>
                <td className="px-4 py-3.5 text-signal">{f.rating.toFixed(1)}</td>
                <td className="px-4 py-3.5">{f.discount_code ? <span className="text-signal">{f.discount_code}</span> : "—"}</td>
                <td className="px-4 py-3.5">{f.challenges?.length || 0}</td>
                <td className="px-4 py-3.5">{f.review_count || 0}</td>
                <td className="px-4 py-3.5">
                  <span className="flex justify-end gap-2">
                    <button onClick={() => openEdit(f)} data-testid={`edit-firm-${f.slug}`}
                      className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-white transition-colors hover:border-signal hover:text-signal">
                      <Pencil size={11} /> Edit
                    </button>
                    <DeleteButton testId={`delete-firm-${f.slug}`} onConfirm={() => remove(f)} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ModalShell wide title={editing === "new" ? "ADD FIRM" : `EDIT — ${editing.name.toUpperCase()}`} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="firm-form">
            <Field label="Firm Name *"><TextInput required data-testid="firm-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Website / Referral URL"><TextInput data-testid="firm-form-url" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://" /></Field>
            <div className="sm:col-span-2"><Field label="Tagline"><TextInput data-testid="firm-form-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></Field></div>
            <div className="sm:col-span-2">
              <Field label="Review Description">
                <textarea rows={3} data-testid="firm-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1.5 w-full resize-none border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-signal" />
              </Field>
            </div>
            <Field label="Logo Image Path (overrides icon)">
              <TextInput data-testid="firm-form-logo" value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} placeholder="/firms/alpha-capital.png or https://…" />
            </Field>
            <Field label="Fallback Icon">
              <select data-testid="firm-form-icon" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}
                className="mt-1.5 w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-signal">
                {ICON_CHOICES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
            <Field label={`Editorial Score (0–5): ${form.rating}`}>
              <input type="range" min="0" max="5" step="0.1" data-testid="firm-form-rating" value={form.rating}
                onChange={(e) => setForm({ ...form, rating: e.target.value })} className="mt-3 w-full accent-profit" />
            </Field>
            <Field label="Category">
              <select data-testid="firm-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1.5 w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-signal">
                {["CFD", "Futures", "Crypto"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Founded"><TextInput value={form.founded} onChange={(e) => setForm({ ...form, founded: e.target.value })} placeholder="2020" /></Field>
            <Field label="Headquarters"><TextInput value={form.headquarters} onChange={(e) => setForm({ ...form, headquarters: e.target.value })} /></Field>
            <Field label="Country"><TextInput data-testid="firm-form-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="USA" /></Field>
            <Field label="Discount Label"><TextInput data-testid="firm-form-discount-label" value={form.discount_label} onChange={(e) => setForm({ ...form, discount_label: e.target.value })} placeholder="20% OFF" /></Field>
            <Field label="Discount Code"><TextInput data-testid="firm-form-discount-code" value={form.discount_code} onChange={(e) => setForm({ ...form, discount_code: e.target.value })} placeholder="FTC" /></Field>
            <Field label="Entry Price"><TextInput value={form.min_price} onChange={(e) => setForm({ ...form, min_price: e.target.value })} placeholder="$49" /></Field>
            <Field label="Max Capital"><TextInput value={form.max_capital} onChange={(e) => setForm({ ...form, max_capital: e.target.value })} placeholder="$400K" /></Field>
            <Field label="Profit Split"><TextInput value={form.profit_split} onChange={(e) => setForm({ ...form, profit_split: e.target.value })} placeholder="Up to 90%" /></Field>
            <Field label="Payout Frequency"><TextInput value={form.payout_frequency} onChange={(e) => setForm({ ...form, payout_frequency: e.target.value })} placeholder="Every 14 days" /></Field>
            <Field label="Prices Verified (date)"><TextInput value={form.prices_verified} onChange={(e) => setForm({ ...form, prices_verified: e.target.value })} placeholder="16 Oct 2026" /></Field>
            <Field label="Total Paid Out"><TextInput value={form.payouts_paid} onChange={(e) => setForm({ ...form, payouts_paid: e.target.value })} placeholder="$10M+" /></Field>
            <Field label="Traders Funded"><TextInput value={form.traders_count} onChange={(e) => setForm({ ...form, traders_count: e.target.value })} placeholder="50K+" /></Field>
            <div className="sm:col-span-2">
              <Field label="Challenges — one per line: Size | Price | Target | Daily DD | Max DD | Model | Bi-Weekly Price | Row Discount (last two optional)">
                <textarea rows={5} data-testid="firm-form-challenges" value={form.challenges} onChange={(e) => setForm({ ...form, challenges: e.target.value })}
                  className="mt-1.5 w-full resize-none border border-line bg-ink px-3 py-2.5 font-mono text-xs text-white outline-none focus:border-signal"
                  placeholder={"$10K | $99 | 10% | 5% | 10% | 2-Step\n$5K | $27 | 6% / 6% | 3% | 6% | 2-Step 6% | $30\n$100K | $499 | None | 3% | 5% | Instant"} />
              </Field>
            </div>
            <div className="sm:col-span-2"><Field label="Pros (comma separated)"><TextInput value={form.pros} onChange={(e) => setForm({ ...form, pros: e.target.value })} /></Field></div>
            <div className="sm:col-span-2"><Field label="Cons (comma separated)"><TextInput value={form.cons} onChange={(e) => setForm({ ...form, cons: e.target.value })} /></Field></div>
            <Field label="Platforms (comma separated)"><TextInput value={form.platforms} onChange={(e) => setForm({ ...form, platforms: e.target.value })} placeholder="MT4, MT5" /></Field>
            <Field label="Instruments (comma separated)"><TextInput value={form.instruments} onChange={(e) => setForm({ ...form, instruments: e.target.value })} placeholder="Forex, Indices" /></Field>
            <label className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted sm:col-span-2">
              <input type="checkbox" data-testid="firm-form-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-profit" />
              Feature on homepage
            </label>
            <div className="sm:col-span-2">
              <button type="submit" disabled={busy} data-testid="firm-form-save"
                className="btn-beam btn-gradient w-full px-5 py-3 font-mono text-[11px] font-700 uppercase tracking-[0.25em] text-white transition-opacity disabled:opacity-50">
                {busy ? "Saving…" : editing === "new" ? "Add To Index" : "Save Changes"}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
}
